/**
 * CanvasTrashZone — a drop-to-delete target that appears at the bottom-right of
 * the canvas WHILE a grid item is being dragged, and removes the item when it's
 * released over the target.
 *
 * Wiring: this is only the VISUAL + geometry. The hit-test + removal go through
 * the grid's cross-grid adapter (`useWebCrossGridDrag`, mounted in SitemapCanvas):
 * SitemapCanvas registers a screen-space zone whose `test(x,y)` reads THIS
 * element's rect (via `rootRef`), and the adapter fires `onDrop` with the zone
 * id on release → the consumer calls `removeItem`. Zones take priority over grid
 * placement, and the adapter cancels the pending move so the delete isn't racing
 * a stale commit (see grid useWebCrossGridDrag zone-drop branch).
 *
 * Visible only for MOVES of existing items (`interaction.draggedItemId`); a
 * palette drag-to-ADD sets no draggedItemId, so the trash stays hidden — you
 * can't delete something you're in the middle of adding. Web-only chrome (the
 * cross-grid adapter is web); `pointerEvents:none` so it never intercepts the
 * drag's own pointer stream — the adapter hit-tests it geometrically.
 */
import { useEffect, useState, type RefObject } from "react";
import { useGridStore } from "@fredericrous/grid";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { spacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import { duration, easing } from "@duro-app/tokens/tokens/motion.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

/** Shared between the zone registration (SitemapCanvas) and this component. */
export const TRASH_ZONE_ID = "canvas-trash";

export function CanvasTrashZone({
  rootRef,
}: {
  rootRef: RefObject<HTMLDivElement | null>;
}) {
  const dragging = useGridStore((s) => s.interaction.gesture.kind === "drag");
  const [hover, setHover] = useState(false);

  // Track hover ourselves: the dragged item holds pointer capture, so the trash
  // element never receives pointerenter — we test the window pointer against our
  // own rect instead. Active only during a drag.
  useEffect(() => {
    if (!dragging) {
      setHover(false);
      return;
    }
    const onMove = (e: PointerEvent) => {
      const el = rootRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHover(
        e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom,
      );
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [dragging, rootRef]);

  if (!dragging) return null;

  return (
    <html.div
      ref={rootRef}
      data-testid="canvas-trash"
      aria-hidden={true}
      style={[s.zone, hover ? s.zoneHover : s.zoneIdle]}
    >
      <html.span aria-hidden={true} style={s.icon}>
        🗑
      </html.span>
      <html.span>
        {hover ? "Release to delete" : "Drop here to delete"}
      </html.span>
    </html.div>
  );
}

const s = css.create({
  zone: {
    position: "fixed",
    right: spacing.lg,
    bottom: spacing.lg,
    zIndex: layers.floating,
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    borderRadius: radii.md,
    borderWidth: borders.strong,
    fontSize: typeScale.fontSize2,
    fontWeight: typography.fontWeightSemibold,
    // pointerEvents:none — the adapter hit-tests this geometrically; letting
    // it swallow pointer events would break the drag it's meant to receive.
    pointerEvents: "none",
    transitionProperty: "transform, background-color, padding",
    transitionDuration: duration.quick,
    transitionTimingFunction: easing.standard,
  },
  zoneIdle: {
    paddingTop: spacing.ms,
    paddingBottom: spacing.ms,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderStyle: "dashed",
    borderColor: colors.border,
    backgroundColor: colors.bgCard,
    color: colors.textMuted,
    boxShadow: shadows.sm,
    transform: "scale(1)",
  },
  zoneHover: {
    // 0.85rem / 1.15rem (13.6 / 18.4px) have no spacing token within ±10%
    // (ms 12, md 16, lg 24): the grown padding stays the authored rem.
    paddingTop: spacing.ms,
    paddingBottom: spacing.ms,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderStyle: "solid",
    borderColor: colors.error,
    backgroundColor: colors.error,
    color: colors.errorContrast,
    boxShadow: shadows.lg,
    transform: "scale(1.06)",
  },
  icon: { fontSize: typography.fontSizeLg, lineHeight: 1 },
});
